/**
 * Service: Evaluation lifecycle data access.
 * Handles creating/loading/updating evaluations in Supabase.
 * All functions throw on error so callers can handle failures.
 */

import { supabase } from "@/integrations/supabase/client";
import { parseCompetencyIds, parseSurveyAnswers } from "@/utils/evaluationParsing";
import { isUUID } from "@/domain/competency";
import { unwrapJoin } from "@/utils/unwrapJoin";
import { handoutDataSchema } from "@/services/validation";
import type { HandoutCustomSection } from "@/domain/handout";

export interface EvaluationDraft {
  id: string;
  evaluation_period_id: string | null;
}

export interface EvaluationHandoutData {
  customSections: Record<string, HandoutCustomSection>;
  masteredCompetencies?: Array<{ id: number | string; name: string; description: string; level?: string }>;
  growingCompetencies?: Array<{ id: number | string; name: string; description: string; level?: string }>;
}

export interface ActiveCompetencyQuestion {
  id: string;
  competency_id: string;
  question_text: string;
  options: { choices?: string[] } | null;
  sort_order: number;
}

/** Fetch evaluation periods for dropdown (only active periods). */
export async function fetchEvaluationPeriodsForDropdown() {
  const { data, error } = await supabase
    .from("evaluation_periods")
    .select("id, name, start_date, end_date, status")
    .in("status", ["active"])
    .order("start_date", { ascending: false });
  if (error) throw new Error(`Impossible de charger les périodes d'évaluation : ${error.message}`);
  return (data ?? []).map((p) => ({
    value: p.id,
    label: p.name,
    from: p.start_date,
    to: p.end_date,
  }));
}

/** Fetch active competency questions for a set of competencies. */
export async function fetchActiveCompetencyQuestions(
  competencyIds: string[],
): Promise<ActiveCompetencyQuestion[]> {
  if (competencyIds.length === 0) return [];

  const { data, error } = await supabase
    .from("competency_questions")
    .select("id, competency_id, question_text, options, sort_order")
    .in("competency_id", competencyIds)
    .eq("is_active", true)
    .order("sort_order");

  if (error) throw new Error(`Impossible de charger les questions de compétences : ${error.message}`);
  return (data ?? []) as ActiveCompetencyQuestion[];
}

/**
 * Apply translations (question_text + mcOption_i) to a list of competency questions
 * for the given language. Returns a new array; falls back to original strings when
 * a translation is missing.
 */
export async function applyCompetencyQuestionTranslations<
  T extends { id: string; question_text: string; options: { choices?: string[] } | null },
>(questions: T[], languageCode: string): Promise<T[]> {
  if (!questions.length || !languageCode) return questions;
  const normalized = languageCode.split("-")[0]?.toLowerCase() || languageCode;
  const { fetchTranslationsByIds } = await import("./competencyTranslationService");
  const map = await fetchTranslationsByIds(
    "competency_question",
    questions.map((q) => q.id),
    normalized,
  );
  if (map.size === 0) return questions;
  return questions.map((q) => {
    const tText = map.get(`${q.id}:question_text`);
    const choices = q.options?.choices;
    let nextOptions = q.options;
    if (Array.isArray(choices) && choices.length > 0) {
      const translatedChoices = choices.map((ch, i) => map.get(`${q.id}:mcOption_${i}`) || ch);
      const changed = translatedChoices.some((ch, i) => ch !== choices[i]);
      if (changed) nextOptions = { ...(q.options || {}), choices: translatedChoices };
    }
    if (!tText && nextOptions === q.options) return q;
    return { ...q, question_text: tText || q.question_text, options: nextOptions };
  });
}

/**
 * Pick the best default period: the one containing today, or the most recent.
 * Periods are already sorted by start_date desc.
 */
export function pickDefaultPeriod(periods: { value: string; from: string; to: string }[]): string | null {
  if (periods.length === 0) return null;
  const today = new Date().toISOString().split("T")[0];
  const containing = periods.find((p) => p.from <= today && p.to >= today);
  return containing ? containing.value : periods[0].value;
}

/** Create a brand new evaluation draft. */
export async function createEvaluationDraft(params: {
  employeeId: string;
  competencyIds: string[];
  periodId: string | null;
  includeCompanyValues?: boolean;
  meetingTemplateId?: string | null;
}): Promise<EvaluationDraft> {
  const insertData: Record<string, unknown> = {
    employee_id: params.employeeId,
    status: "draft",
    survey_answers: {},
    competency_ids: params.competencyIds,
    current_step: 1,
    include_company_values: params.includeCompanyValues ?? false,
  };
  if (params.periodId) insertData.evaluation_period_id = params.periodId;
  if (params.meetingTemplateId) insertData.meeting_template_id = params.meetingTemplateId;

  const { data, error } = await supabase
    .from("evaluations")
    .insert(insertData)
    .select("id, evaluation_period_id")
    .single();

  if (error) throw new Error(`Impossible de créer le brouillon d'évaluation : ${error.message}`);
  return data as EvaluationDraft;
}

/** Fetch an existing evaluation by ID or find the latest draft for an employee. */
export async function fetchEvaluationDraft(
  evaluationId: string | null,
  employeeId: string,
): Promise<EvaluationDraft | null> {
  if (evaluationId) {
    const { data, error } = await supabase
      .from("evaluations")
      .select("id, evaluation_period_id")
      .eq("id", evaluationId)
      .eq("employee_id", employeeId)
      .maybeSingle();

    if (error) throw new Error(`Impossible de charger le brouillon d'évaluation : ${error.message}`);
    if (data) return data as EvaluationDraft;
  }

  const { data, error } = await supabase
    .from("evaluations")
    .select("id, evaluation_period_id, competency_ids")
    .eq("employee_id", employeeId)
    .eq("status", "draft")
    .order("created_at", { ascending: false })
    .limit(10);

  if (error) throw new Error(`Impossible de charger le brouillon d'évaluation : ${error.message}`);

  const drafts = data ?? [];
  const draftWithCompetencies = drafts.find(
    (d) => Array.isArray(d.competency_ids) && d.competency_ids.length > 0,
  );

  const selected = draftWithCompetencies ?? drafts[0];
  if (!selected) return null;

  return {
    id: selected.id,
    evaluation_period_id: selected.evaluation_period_id,
  };
}

/** Update competency_ids on an existing evaluation if they changed. */
export async function updateEvaluationCompetencyIds(
  evaluationId: string,
  newIds: string[],
): Promise<boolean> {
  const { data: existing, error: fetchError } = await supabase
    .from("evaluations")
    .select("competency_ids")
    .eq("id", evaluationId)
    .single();

  if (fetchError) throw new Error(`Impossible de lire les compétences de l'évaluation : ${fetchError.message}`);

  const storedIds = existing?.competency_ids;
  const changed =
    !storedIds ||
    !Array.isArray(storedIds) ||
    storedIds.length !== newIds.length ||
    !newIds.every((id: string) => storedIds.includes(id));

  if (changed) {
    const { error } = await supabase
      .from("evaluations")
      .update({ competency_ids: newIds })
      .eq("id", evaluationId);
    if (error) throw new Error(`Impossible de mettre à jour les compétences : ${error.message}`);
  }

  return changed;
}

/** Update the evaluation period on an existing evaluation. */
export async function updateEvaluationPeriod(
  evaluationId: string,
  periodId: string,
  competencyIds?: string[],
) {
  const updateData: { evaluation_period_id: string; competency_ids?: string[] } = { evaluation_period_id: periodId };
  if (competencyIds && competencyIds.length > 0) updateData.competency_ids = competencyIds;
  const { error } = await supabase.from("evaluations").update(updateData).eq("id", evaluationId);
  if (error) throw new Error(`Impossible de mettre à jour la période : ${error.message}`);
}

/** Create a new evaluation with a specific period. */
export async function createEvaluationWithPeriod(
  employeeId: string,
  periodId: string,
  competencyIds?: string[],
): Promise<string> {
  const insertData: { employee_id: string; evaluation_period_id: string; status: string; competency_ids?: string[] } = {
    employee_id: employeeId,
    evaluation_period_id: periodId,
    status: "draft",
  };
  if (competencyIds) insertData.competency_ids = competencyIds;

  const { data, error } = await supabase.from("evaluations").insert(insertData).select("id").single();
  if (error) throw new Error(`Impossible de créer l'évaluation : ${error.message}`);
  return data.id;
}

/** Update evaluation step. */
export async function updateEvaluationStep(evaluationId: string, step: number) {
  const { error } = await supabase
    .from("evaluations")
    .update({ current_step: step })
    .eq("id", evaluationId);
  if (error) throw new Error(`Impossible de mettre à jour l'étape : ${error.message}`);
}

export async function updateEvaluationHandoutData(
  evaluationId: string,
  handoutData: EvaluationHandoutData,
): Promise<void> {
  const parsed = handoutDataSchema.parse(handoutData);
  const { error } = await supabase
    .from("evaluations")
    .update({ handout_data: parsed } as never)
    .eq("id", evaluationId);
  if (error) throw new Error(`Impossible de sauvegarder le document de remise : ${error.message}`);
}

/** Mark an evaluation as completed. */
export async function completeEvaluation(evaluationId: string): Promise<void> {
  const { error } = await supabase
    .from("evaluations")
    .update({ status: "completed" as never, submitted_at: new Date().toISOString() })
    .eq("id", evaluationId);
  if (error) throw new Error(`Impossible de finaliser l'évaluation : ${error.message}`);
}

/** Delete an evaluation and its related topics. */
export async function deleteEvaluation(evaluationId: string): Promise<void> {
  const { error: topicsError } = await supabase
    .from("evaluation_topics")
    .delete()
    .eq("evaluation_id", evaluationId);
  if (topicsError) throw new Error(`Impossible de supprimer les sujets : ${topicsError.message}`);

  const { error } = await supabase
    .from("evaluations")
    .delete()
    .eq("id", evaluationId);
  if (error) throw new Error(`Impossible de supprimer l'évaluation : ${error.message}`);
}

/** Revert a completed evaluation back to draft. */
export async function uncompleteEvaluation(evaluationId: string): Promise<void> {
  const { error } = await supabase
    .from("evaluations")
    .update({ status: "draft" as never, submitted_at: null })
    .eq("id", evaluationId);
  if (error) throw new Error(`Impossible de réouvrir l'évaluation : ${error.message}`);
}

/**
 * Update evaluation competency IDs and clean survey answers
 * by removing answers for competencies that are no longer part of the evaluation.
 * Question-level answers linked to the remaining competencies are preserved.
 */
export async function updateEvaluationCompetencies(
  evaluationId: string,
  newIds: string[],
): Promise<void> {
  const { data: evalData, error: fetchError } = await supabase
    .from("evaluations")
    .select("survey_answers")
    .eq("id", evaluationId)
    .single();

  if (fetchError) throw new Error(`Impossible de lire les réponses : ${fetchError.message}`);

  const existingAnswers = (evalData?.survey_answers as Record<string, number>) || {};
  const cleanedAnswers: Record<string, number> = {};
  const allowedSet = new Set(newIds);
  const activeQuestions = await fetchActiveCompetencyQuestions(newIds);
  const allowedQuestionIds = new Set(activeQuestions.map((question) => question.id));
  Object.entries(existingAnswers).forEach(([key, val]) => {
    if (allowedSet.has(key) || allowedQuestionIds.has(key) || key.startsWith("value-")) {
      cleanedAnswers[key] = val;
    }
  });

  const { error } = await supabase
    .from("evaluations")
    .update({
      competency_ids: newIds,
      survey_answers: cleanedAnswers,
    })
    .eq("id", evaluationId);
  if (error) throw new Error(`Impossible de mettre à jour les compétences : ${error.message}`);
}

// ─── Evaluation Recovery ─────────────────────────────────────────────

export interface RecoveredEvaluationData {
  employeeId: string;
  evaluationPeriodId: string | null;
  competencyIds: string[];
  /** Filtered survey_answers: only keys matching competencyIds, active question ids or value- prefix are kept. */
  filteredSurveyAnswers: Record<string, number>;
  includeCompanyValues: boolean;
  surveyNotes?: Record<string, string>;
  currentStep?: number;
}

/**
 * Fetch employee ID, competency IDs and filtered survey_answers from an evaluation record.
 * Business rule: competency_ids is the authoritative source;
 * falls back to survey_answers UUID keys if competency_ids is empty.
 * survey_answers are always filtered to only keep authoritative competency keys + active question ids + value- keys.
 */
export async function fetchEvaluationContext(
  evaluationId: string,
): Promise<RecoveredEvaluationData | null> {
  const { data, error } = await supabase
    .from("evaluations")
    .select("employee_id, evaluation_period_id, competency_ids, survey_answers, manual_overrides, include_company_values, survey_notes, current_step")
    .eq("id", evaluationId)
    .single();

  if (error) throw new Error(`Impossible de charger le contexte d'évaluation : ${error.message}`);
  if (!data) return null;

  const employeeId = data.employee_id;
  const evaluationPeriodId = data.evaluation_period_id ?? null;
  const includeCompanyValues = (data as Record<string, unknown>).include_company_values === true;
  const surveyNotes = (data as Record<string, unknown>).survey_notes as Record<string, string> | null;
  const currentStep = (data as Record<string, unknown>).current_step as number | null;

  let competencyIds = parseCompetencyIds(data.competency_ids);

  // Parse raw answers once and recover answered competency UUIDs.
  const rawAnswers = parseSurveyAnswers(data.survey_answers);
  const answerUUIDs = Object.keys(rawAnswers).filter(isUUID);

  // Resolve question IDs → competency IDs.
  // Some surveys store answers keyed by competency_questions.id rather than competencies.id.
  // We need to remap those back to competency IDs so the results page can match them.
  let questionToCompetency: Record<string, string> = {};
  if (answerUUIDs.length > 0) {
    const { data: qRows } = await supabase
      .from("competency_questions")
      .select("id, competency_id")
      .in("id", answerUUIDs);
    if (qRows && qRows.length > 0) {
      questionToCompetency = Object.fromEntries(qRows.map(q => [q.id, q.competency_id]));
    }
  }

  // Remap: convert question-ID keys to competency-ID keys (average when multiple questions per competency).
  // IMPORTANT: we keep the ORIGINAL question-keyed answers as well so downstream
  // consumers (e.g. MeetingPlan) can re-aggregate with their own rule (floor),
  // instead of being forced to trust a rounded competency-level average that
  // can inflate the displayed mastery (e.g. avg 2.5 → round → 3 = "achieved").
  const remappedAnswers: Record<string, number> = { ...rawAnswers };
  const competencyScoreSums: Record<string, { sum: number; count: number }> = {};
  Object.entries(rawAnswers).forEach(([key, val]) => {
    const mappedCompId = questionToCompetency[key];
    if (mappedCompId) {
      if (!competencyScoreSums[mappedCompId]) competencyScoreSums[mappedCompId] = { sum: 0, count: 0 };
      competencyScoreSums[mappedCompId].sum += val;
      competencyScoreSums[mappedCompId].count += 1;
    }
  });
  // Write floored aggregated scores for remapped competencies (conservative:
  // requires every underlying question to score at the level for the
  // competency to reach that level).
  Object.entries(competencyScoreSums).forEach(([compId, { sum, count }]) => {
    if (remappedAnswers[compId] === undefined) {
      remappedAnswers[compId] = Math.floor(sum / count);
    }
  });

  const answeredCompetencyIds = Object.keys(remappedAnswers).filter(isUUID);

  // If competency_ids is empty, recover from survey answers.
  if (competencyIds.length === 0) {
    competencyIds = answeredCompetencyIds;
  } else if (answeredCompetencyIds.length > 0) {
    competencyIds = Array.from(new Set([...competencyIds, ...answeredCompetencyIds]));
  }

  // Filter survey_answers to only keep allowed keys
  const allowedSet = new Set(competencyIds);
  const activeQuestions = await fetchActiveCompetencyQuestions(competencyIds);
  const allowedQuestionIds = new Set(activeQuestions.map((question) => question.id));
  const filteredSurveyAnswers: Record<string, number> = {};
  Object.entries(remappedAnswers).forEach(([key, val]) => {
    if (allowedSet.has(key) || allowedQuestionIds.has(key) || key.startsWith("value-")) {
      filteredSurveyAnswers[key] = val;
    }
  });

  return { employeeId, evaluationPeriodId, competencyIds, filteredSurveyAnswers, includeCompanyValues, surveyNotes: surveyNotes ?? undefined, currentStep: currentStep ?? undefined };
}

// ─── Draft Evaluations ───────────────────────────────────────────────

export interface DraftEvaluationSummary {
  id: string;
  employee_id: string;
  evaluation_period_id: string;
  current_step: number;
  completion_progress: number;
  is_thematic_meeting: boolean;
  created_at: string;
  status?: string;
  submitted_at?: string | null;
  employee: {
    first_name: string; last_name: string; role_title: string | null;
    avatar_url: string | null; work_location: string | null; department: string | null;
    hire_date: string | null; birthday: string | null; manager_id: string | null;
  } | null;
  period: { name: string; start_date: string; end_date: string } | null;
  meeting_template: { id: string; title: string; topic_label: string | null } | null;
}

function hasText(value: string | null | undefined): boolean {
  return typeof value === "string" && value.trim().length > 0;
}

async function calculateManagerCompletionProgress(draft: {
  id: string;
  competency_ids: unknown;
  survey_answers: unknown;
}): Promise<number> {
  const competencyIds = parseCompetencyIds(draft.competency_ids);
  const surveyAnswers = parseSurveyAnswers(draft.survey_answers);

  const activeQuestions = await fetchActiveCompetencyQuestions(competencyIds);
  const questionsByCompetency = new Map<string, ActiveCompetencyQuestion[]>();
  activeQuestions.forEach((question) => {
    const list = questionsByCompetency.get(question.competency_id) ?? [];
    list.push(question);
    questionsByCompetency.set(question.competency_id, list);
  });

  const expectedQuestionKeys = competencyIds.flatMap((competencyId) => {
    const questions = questionsByCompetency.get(competencyId);
    return questions && questions.length > 0 ? questions.map((question) => question.id) : [competencyId];
  });

  const answeredSurveyQuestions = expectedQuestionKeys.filter((key) => surveyAnswers[key] !== undefined).length;
  const surveyProgress = expectedQuestionKeys.length > 0
    ? Math.round((answeredSurveyQuestions / expectedQuestionKeys.length) * 80)
    : 0;

  const { data: topics, error } = await supabase
    .from("evaluation_topics")
    .select("title, details, response")
    .eq("evaluation_id", draft.id);
  if (error) throw new Error(`Impossible de calculer la progression de l'évaluation : ${error.message}`);

  const step3WasModified = (topics ?? []).some((topic) => hasText(topic.title) || hasText(topic.details));
  const step4WasModified = (topics ?? []).some((topic) => hasText(topic.response));

  return Math.min(surveyProgress + (step3WasModified ? 10 : 0) + (step4WasModified ? 5 : 0), 95);
}

/** Fetch all draft evaluations with employee and period info. */
export async function fetchDraftEvaluations(): Promise<DraftEvaluationSummary[]> {
  const { data, error } = await supabase
    .from("evaluations")
    .select("id, employee_id, evaluation_period_id, current_step, survey_answers, competency_ids, created_at, status, submitted_at, meeting_template_id, employees!evaluations_employee_id_fkey(first_name, last_name, role_title, avatar_url, work_location, hire_date, birthday, manager_id, departments(name)), evaluation_periods!evaluations_evaluation_period_id_fkey(name, start_date, end_date), meeting_plan_templates!evaluations_meeting_template_id_fkey(id, title, topic_label)")
    .eq("status", "draft")
    .order("created_at", { ascending: false });
  if (error) throw new Error(`Impossible de charger les brouillons : ${error.message}`);
  return Promise.all((data ?? []).map(async (d) => {
    const emp = unwrapJoin<{ first_name: string; last_name: string; role_title: string | null; avatar_url: string | null; work_location: string | null; hire_date: string | null; birthday: string | null; manager_id: string | null; departments: { name: string } | null }>(d.employees);
    const per = unwrapJoin<{ name: string; start_date: string; end_date: string }>(d.evaluation_periods);
    const tpl = unwrapJoin<{ id: string; title: string; topic_label: string | null }>((d as { meeting_plan_templates?: unknown }).meeting_plan_templates);
    return {
      id: d.id,
      employee_id: d.employee_id,
      evaluation_period_id: d.evaluation_period_id,
      current_step: d.current_step || 1,
      completion_progress: await calculateManagerCompletionProgress(d),
      is_thematic_meeting: parseCompetencyIds(d.competency_ids).length === 0,
      created_at: d.created_at,
      status: d.status,
      submitted_at: d.submitted_at,
      employee: emp ? { first_name: emp.first_name, last_name: emp.last_name, role_title: emp.role_title, avatar_url: emp.avatar_url, work_location: emp.work_location, department: emp.departments?.name ?? null, hire_date: emp.hire_date, birthday: emp.birthday, manager_id: emp.manager_id } : null,
      period: per ?? null,
      meeting_template: tpl ?? null,
    };
  }));
}

/** Fetch all completed evaluations with employee and period info. */
export async function fetchCompletedEvaluations(): Promise<DraftEvaluationSummary[]> {
  const { data, error } = await supabase
    .from("evaluations")
    .select("id, employee_id, evaluation_period_id, competency_ids, current_step, created_at, status, submitted_at, meeting_template_id, employees!evaluations_employee_id_fkey(first_name, last_name, role_title, avatar_url, work_location, hire_date, birthday, manager_id, departments(name)), evaluation_periods!evaluations_evaluation_period_id_fkey(name, start_date, end_date), meeting_plan_templates!evaluations_meeting_template_id_fkey(id, title, topic_label)")
    .eq("status", "completed")
    .order("submitted_at", { ascending: false });
  if (error) throw new Error(`Impossible de charger les évaluations complétées : ${error.message}`);
  return (data ?? []).map((d) => {
    const emp = unwrapJoin<{ first_name: string; last_name: string; role_title: string | null; avatar_url: string | null; work_location: string | null; hire_date: string | null; birthday: string | null; manager_id: string | null; departments: { name: string } | null }>(d.employees);
    const per = unwrapJoin<{ name: string; start_date: string; end_date: string }>(d.evaluation_periods);
    const tpl = unwrapJoin<{ id: string; title: string; topic_label: string | null }>((d as { meeting_plan_templates?: unknown }).meeting_plan_templates);
    return {
      id: d.id,
      employee_id: d.employee_id,
      evaluation_period_id: d.evaluation_period_id,
      current_step: d.current_step || 1,
      completion_progress: 100,
      is_thematic_meeting: parseCompetencyIds(d.competency_ids).length === 0,
      created_at: d.created_at,
      status: d.status,
      submitted_at: d.submitted_at,
      employee: emp ? { first_name: emp.first_name, last_name: emp.last_name, role_title: emp.role_title, avatar_url: emp.avatar_url, work_location: emp.work_location, department: emp.departments?.name ?? null, hire_date: emp.hire_date, birthday: emp.birthday, manager_id: emp.manager_id } : null,
      period: per ?? null,
      meeting_template: tpl ?? null,
    };
  });
}

/**
 * Latest completed evaluation for a given employee, if any.
 * Returns null when none exists.
 */
export async function fetchLatestCompletedEvaluationId(
  employeeId: string,
): Promise<string | null> {
  const { data, error } = await supabase
    .from("evaluations")
    .select("id")
    .eq("employee_id", employeeId)
    .eq("status", "completed")
    .order("submitted_at", { ascending: false, nullsFirst: false })
    .limit(1)
    .maybeSingle();
  if (error) throw new Error(`Impossible de charger la dernière évaluation : ${error.message}`);
  return data?.id ?? null;
}

/** Fetch survey answers for an evaluation (filtered by allowed competency IDs and active question IDs). */
export async function fetchFilteredSurveyAnswers(
  evaluationId: string,
  allowedCompetencyIds: string[],
): Promise<Record<string, number>> {
  const { data, error } = await supabase
    .from("evaluations")
    .select("survey_answers")
    .eq("id", evaluationId)
    .single();
  if (error) throw new Error(`Impossible de charger les réponses : ${error.message}`);
  if (!data?.survey_answers || typeof data.survey_answers !== "object") return {};
  const raw = data.survey_answers as Record<string, number>;
  const activeQuestions = await fetchActiveCompetencyQuestions(allowedCompetencyIds);
  const allowedQuestionIds = new Set(activeQuestions.map((question) => question.id));
  const filtered: Record<string, number> = {};
  Object.entries(raw).forEach(([key, val]) => {
    if (allowedQuestionIds.has(key) || allowedCompetencyIds.includes(key) || key.startsWith("value-")) {
      filtered[key] = val;
    }
  });
  return filtered;
}

// ─── Manual Overrides ──────────────────────────────────────────

export interface ManualOverride {
  status: string; // ui status: notMet | evolution | almostAchieved | achieved
  notes?: string;
}

/** Fetch manual overrides for an evaluation. */
export async function fetchManualOverrides(evaluationId: string): Promise<Record<string, ManualOverride>> {
  const { data, error } = await supabase
    .from("evaluations")
    .select("manual_overrides")
    .eq("id", evaluationId)
    .single();
  if (error) throw new Error(`Impossible de charger les surcharges manuelles : ${error.message}`);
  return (data?.manual_overrides as Record<string, ManualOverride>) || {};
}

/** Save a manual override for a single competency on an evaluation. */
export async function saveManualOverride(
  evaluationId: string,
  competencyId: string,
  override: ManualOverride,
): Promise<void> {
  const existing = await fetchManualOverrides(evaluationId);
  const updated = { ...existing, [competencyId]: override };
  const { error } = await supabase
    .from("evaluations")
    .update({ manual_overrides: updated })
    .eq("id", evaluationId);
  if (error) throw new Error(`Impossible de sauvegarder la surcharge manuelle : ${error.message}`);
}

// ─── Evaluation Period CRUD ────────────────────────────────────

export interface EvaluationPeriodRow {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  status: "active" | "draft" | "inactive";
  organization_id: string;
}

/** Fetch all evaluation periods for the current org. */
export async function fetchEvaluationPeriods(): Promise<EvaluationPeriodRow[]> {
  const { data, error } = await supabase
    .from("evaluation_periods")
    .select("id, name, start_date, end_date, status, organization_id")
    .order("start_date", { ascending: false });
  if (error) throw new Error(`Impossible de charger les périodes : ${error.message}`);
  return (data ?? []) as EvaluationPeriodRow[];
}

/** Create a new evaluation period. */
export async function createEvaluationPeriod(params: {
  name: string;
  startDate: string;
  endDate: string;
  status: "active" | "draft" | "inactive";
  organizationId: string;
}): Promise<EvaluationPeriodRow> {
  const { data, error } = await supabase
    .from("evaluation_periods")
    .insert({
      name: params.name,
      start_date: params.startDate,
      end_date: params.endDate,
      status: params.status,
      organization_id: params.organizationId,
    })
    .select("id, name, start_date, end_date, status, organization_id")
    .single();
  if (error) throw new Error(`Impossible de créer la période : ${error.message}`);
  return data as EvaluationPeriodRow;
}

/** Update an existing evaluation period record. */
export async function updateEvaluationPeriodRecord(
  id: string,
  updates: Partial<{ name: string; start_date: string; end_date: string; status: string }>,
): Promise<void> {
  const { error } = await supabase
    .from("evaluation_periods")
    .update(updates)
    .eq("id", id);
  if (error) throw new Error(`Impossible de mettre à jour la période : ${error.message}`);
}

/** Delete an evaluation period. */
export async function deleteEvaluationPeriod(id: string): Promise<void> {
  const { error } = await supabase
    .from("evaluation_periods")
    .delete()
    .eq("id", id);
  if (error) throw new Error(`Impossible de supprimer la période : ${error.message}`);
}

/** Fetch a single cycle including its launched config snapshot (if any). */
export async function fetchEvaluationPeriodFull(id: string): Promise<{
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  status: "active" | "draft" | "inactive";
  organization_id: string;
  launched_at: string | null;
  launched_config: unknown | null;
  draft_config: unknown | null;
} | null> {
  const { data, error } = await supabase
    .from("evaluation_periods")
    .select("id, name, start_date, end_date, status, organization_id, launched_at, launched_config, draft_config")
    .eq("id", id)
    .maybeSingle();
  if (error) throw new Error(`Impossible de charger le cycle : ${error.message}`);
  return (data as unknown) as Awaited<ReturnType<typeof fetchEvaluationPeriodFull>>;
}

/** Persist the in-progress (draft) configuration snapshot for a cycle. */
export async function saveCycleDraftConfig(
  id: string,
  snapshot: unknown,
): Promise<void> {
  const { error } = await supabase
    .from("evaluation_periods")
    .update({ draft_config: snapshot } as Record<string, unknown>)
    .eq("id", id);
  if (error) throw new Error(`Impossible d'enregistrer le brouillon : ${error.message}`);
}

/** Mark a cycle as launched (active) and persist a snapshot of its configuration. */
export async function launchEvaluationCycleRecord(
  id: string,
  snapshot: unknown,
): Promise<void> {
  const { error } = await supabase
    .from("evaluation_periods")
    .update({
      status: "active",
      launched_at: new Date().toISOString(),
      launched_config: snapshot,
    } as Record<string, unknown>)
    .eq("id", id);
  if (error) throw new Error(`Impossible de lancer le cycle : ${error.message}`);
}

// ─── Company Values Flag ─────────────────────────────────────────

/** Fetch include_company_values flag for an evaluation. */
export async function fetchEvaluationIncludeValues(evaluationId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from("evaluations")
    .select("include_company_values")
    .eq("id", evaluationId)
    .single();
  if (error) throw new Error(`Impossible de charger le flag valeurs : ${error.message}`);
  return (data as Record<string, unknown>)?.include_company_values === true;
}

/** Update include_company_values flag on an evaluation. */
export async function updateEvaluationIncludeValues(evaluationId: string, include: boolean): Promise<void> {
  const { error } = await supabase
    .from("evaluations")
    .update({ include_company_values: include } as Record<string, unknown>)
    .eq("id", evaluationId);
  if (error) throw new Error(`Impossible de mettre à jour le flag valeurs : ${error.message}`);
}

// ─── Survey Notes & Position Persistence ─────────────────────────────

/**
 * Persist survey notes (Record<competencyId, noteText>) on the evaluation.
 * Uses an atomic server-side merge so concurrent sessions never erase each
 * other's notes — only the keys present in `notes` are written.
 */
export async function updateSurveyNotes(evaluationId: string, notes: Record<string, string>): Promise<void> {
  const { error } = await supabase.rpc("merge_survey_data", {
    _evaluation_id: evaluationId,
    _answers: null,
    _notes: notes as unknown as Record<string, unknown>,
    _current_step: null,
  } as never);
  if (error) throw new Error(`Impossible de sauvegarder les notes du sondage : ${error.message}`);
}

/**
 * Persist current question index using the existing current_step column.
 * The server-side function only advances the index forward, never backwards,
 * so a stale tab cannot rewind progress made elsewhere.
 */
export async function updateCurrentQuestionIndex(evaluationId: string, index: number): Promise<void> {
  const { error } = await supabase.rpc("merge_survey_data", {
    _evaluation_id: evaluationId,
    _answers: null,
    _notes: null,
    _current_step: index,
  } as never);
  if (error) throw new Error(`Impossible de sauvegarder la position : ${error.message}`);
}
